// @flow
import { API } from "aws-amplify";

import React from "react";
import { useEffect, useState, useContext, useRef } from "react";

import {
  queryShortcutBindingsByKeyBindingSessionIdIndex,
  queryUserMacrosByUserIdTriggerWordIndex,
} from "./../graphql/queries";
import {
  onUpdateShortcutBindingBySession,
  onCreateUserMacro,
  onUpdateUserMacro,
  onDeleteUserMacro,
} from "./../graphql/subscriptions";
import { createShortcutBinding as createShortcutBindingMutation } from "../graphql/mutations";

import {
  Container,
  Row,
  Col,
  Card,
  Button,
  InputGroup,
  Collapse,
} from "react-bootstrap";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as ChevronDown } from "bootstrap-icons/icons/chevron-down.svg";
import { ReactComponent as ChevronUp } from "bootstrap-icons/icons/chevron-up.svg";

import * as Sentry from "@sentry/react";
import UserContext from "./../utils/UserContext";
import EditShortcutsView from "./EditShortcutsView.react";
import "./ShortcutsView.css";

type Props = $ReadOnly<{
  sessionId: string,
  setShortcutInput: React.Dispatch<React.SetStateAction<any>>,
  setShortcutInputIndex: React.Dispatch<React.SetStateAction<any>>,
}>;

const DEFAULT_TOTAL_HEIGHT = 750;
const DEFAULT_SHORTCUT_HEIGHT = 400;
const DEFAULT_MACRO_HEIGHT = 500;
// 그룹 단위 토글은 매크로 수만큼 구독 이벤트가 오므로 마지막 것만 fetch한다
const MACRO_REFETCH_DEBOUNCE_MS = 300;

const styles = {
  shortcutButton: {
    textAlign: "left",
    fontSize: "0.9rem",
    padding: "1.1px",
    paddingLeft: "5px",
  },
  macroItems: {
    color: "#3e3e3eff",
    fontSize: "0.9rem",
    padding: "1px 4px",
    cursor: "default",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%", // 여기서 다시 100%로 강제
    flex: 1,
  },
  inputGroup: {
    cursor: "default",
  },
  groupName: {
    fontSize: "0.9rem",
    cursor: "default",
    fontWeight: 600,
    borderBottom: "1px solid #cdcdcdff",
  },
  listContainer: { paddingBottom: "10px" },
};

async function fetchBindings(sessionId) {
  try {
    const apiData = await API.graphql({
      query: queryShortcutBindingsByKeyBindingSessionIdIndex,
      variables: {
        keyBindingSessionId: sessionId,
      },
    });
    return (
      apiData.data?.queryShortcutBindingsByKeyBindingSessionIdIndex?.items ?? []
    );
  } catch (e) {
    console.log(e);
  }
}

async function createShortcutBinding(
  sessionId: string,
  index: number,
  key,
  text: string
) {
  if (sessionId === "") {
    return;
  }
  const ret = await API.graphql({
    query: createShortcutBindingMutation,
    variables: {
      input: {
        keyBindingSessionId: sessionId,
        index: index,
        key: key,
        ctrl: true,
        text: text,
      },
    },
  });
}

async function fetchUserMacro(userId) {
  try {
    const apiData = await API.graphql({
      query: queryUserMacrosByUserIdTriggerWordIndex,
      variables: {
        userId: userId,
      },
    });
    return apiData.data.queryUserMacrosByUserIdTriggerWordIndex.items;
  } catch (e) {
    console.log(e);
  }
}

export default function ShortcutsView(props: Props): React$Element<any> {
  const [showShortcutEditModal, setShowShortcutEditModal] = useState(false);
  const [showMacroEditModal, setShowMacroEditModal] = useState(false);
  const [bindings, setBindings] = useState([]);
  const [macroArray, setMacroArray] = useState([]);
  const [macroGroupArray, setMacroGroupArray] = useState([]);
  const [isShortcutCollapsed, setIsShortcutCollapsed] = useState(false);
  const [isMacroCollapsed, setIsMacroCollapsed] = useState(false);
  const [showMacroScrollbar, setShowMacroScrollbar] = useState(false);
  const [shortcutHeight, setShortcutHeight] = useState(DEFAULT_SHORTCUT_HEIGHT);
  const [macroHeight, setMacroHeight] = useState(DEFAULT_MACRO_HEIGHT);

  const userContext = useContext(UserContext);
  const macroScrollHideTimeoutRef = useRef<?TimeoutID>(null);
  const macroRefetchTimeoutRef = useRef<?TimeoutID>(null);

  // Initial fetch, create shortcut bindings when the session is first loaded
  useEffect(() => {
    async function initialFetch() {
      const fetchedBindings = await fetchBindings(props.sessionId);
      if (fetchedBindings.length === 0) {
        for (let i = 0; i < 10; i++) {
          if (i === 9) {
            createShortcutBinding(props.sessionId, i + 1, 0, "");
          } else {
            createShortcutBinding(props.sessionId, i + 1, i + 1, "");
          }
        }
        setTimeout(() => {
          async function initialFetch() {
            const fetchedBindings = await fetchBindings(props.sessionId);
            setBindings(fetchedBindings);
          }
          initialFetch();
        }, 1000);
      } else {
        setBindings(fetchedBindings);
      }
    }
    initialFetch();
  }, [props.sessionId]);

  // Subscribe to update shortcut by session
  useEffect(() => {
    const subscription = API.graphql({
      query: onUpdateShortcutBindingBySession,
      variables: {
        keyBindingSessionId: props.sessionId,
      },
    }).subscribe({
      next: () => {
        async function updateFetch() {
          const fetchedBindings = await fetchBindings(props.sessionId);
          setBindings(fetchedBindings);
        }
        updateFetch();
      },
      error: (error) => console.warn(error),
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Initial macro fetch
  useEffect(() => {
    handleFetchMacro();
  }, []);

  // Subscribe to macro mutations (create/update/delete) - with reconnect
  useEffect(() => {
    let isMounted = true;
    const subsRef = []; // 현재 살아있는 subscription들

    const subscribeAll = () => {
      // 기존 것들 먼저 정리
      subsRef.forEach((s) => s?.unsubscribe?.());
      subsRef.length = 0;

      if (!userContext.userId) return;

      const subscribeOne = (query) => {
        const obs = API.graphql({
          query,
          variables: { userId: userContext.userId },
        });

        const sub = obs.subscribe({
          next: () => {
            handleFetchMacroDebounced();
          },
          error: (err) => {
            console.warn("[ShortcutsView] macro subscription error", err);
            const message =
              err?.error?.errors?.[0]?.message ?? err?.errors?.[0]?.message;
            Sentry.captureException(
              new Error(message ?? "ShortcutsView macro subscription error"),
              {
                extra: {
                  context: "ShortcutsView macro subscription",
                  rawError: err,
                },
              }
            );
            if (!isMounted) return;

            if (message === "Connection closed") {
              setTimeout(() => {
                if (isMounted) {
                  console.log(
                    "[ShortcutsView] Re-subscribing macro mutations..."
                  );
                  subscribeAll();
                }
              }, 1000);
            }
          },
        });

        subsRef.push(sub);
      };

      subscribeOne(onCreateUserMacro);
      subscribeOne(onUpdateUserMacro);
      subscribeOne(onDeleteUserMacro);
    };

    subscribeAll();

    return () => {
      isMounted = false;
      subsRef.forEach((s) => s?.unsubscribe?.());
      subsRef.length = 0;
      if (macroRefetchTimeoutRef.current) {
        clearTimeout(macroRefetchTimeoutRef.current);
      }
    };
  }, [userContext.userId]);

  // Calculate macro view height dynamically
  useEffect(() => {
    if (isShortcutCollapsed && !isMacroCollapsed) {
      setMacroHeight(DEFAULT_TOTAL_HEIGHT);
    } else {
      setMacroHeight(DEFAULT_MACRO_HEIGHT);
    }
  }, [isShortcutCollapsed, isMacroCollapsed]);

  const macroByGroup = (group, macro) => {
    if (group === macro.macroGroup && macro.isEnabled) {
      return (
        <Row>
          <Col xs={12}>
            <div
              className="macro-item"
              style={styles.macroItems}
              onClick={() => {
                props.setShortcutInput(macro.insertWord);
                props.setShortcutInputIndex(
                  (prevShortcutInputIndex) => prevShortcutInputIndex + 1
                );
              }}
            >
              <span>{macro.macroName}</span>
              <span>{macro.triggerWord}</span>
            </div>
          </Col>
        </Row>
      );
    }
  };

  const macroList = macroGroupArray.map((group, idx) => (
    <Container key={idx} className="m-0 p-0" style={styles.listContainer}>
      <div style={styles.groupName}>{group}</div>
      {macroArray.map((macro, idx) => (
        <div key={idx}>{macroByGroup(group, macro, idx)}</div>
      ))}
    </Container>
  ));

  const makeMacroGroupArray = (macro: array) => {
    let arr = [];
    for (let i = 0; i < macro.length; i++) {
      if (!arr.includes(macro[i].macroGroup) && macro[i].isEnabled) {
        arr.push(macro[i].macroGroup);
      }
    }
    arr.sort();
    setMacroGroupArray(arr);
  };

  const handleFetchMacro = () => {
    async function updateFetch() {
      const macro = await fetchUserMacro(userContext.userId);
      setMacroArray(macro);
      makeMacroGroupArray(macro);
    }
    updateFetch();
  };

  // 그룹 토글처럼 이벤트가 연달아 오는 경우 마지막 것만 fetch한다
  const handleFetchMacroDebounced = () => {
    if (macroRefetchTimeoutRef.current) {
      clearTimeout(macroRefetchTimeoutRef.current);
    }
    macroRefetchTimeoutRef.current = setTimeout(
      handleFetchMacro,
      MACRO_REFETCH_DEBOUNCE_MS
    );
  };

  // [ ====== Auto Hide Scroll Bar Handlers Start ====== ]
  // 스크롤 시 잠깐 동안만 스크롤바 보이게
  const handleMacroScroll = () => {
    setShowMacroScrollbar(true);

    if (macroScrollHideTimeoutRef.current) {
      clearTimeout(macroScrollHideTimeoutRef.current);
    }
    macroScrollHideTimeoutRef.current = setTimeout(() => {
      setShowMacroScrollbar(false);
    }, 800); // 0.8초 정도 후에 숨김
  };

  // 오른쪽 "스크롤/리사이즈 영역"에 마우스 들어갔을 때 표시
  const handleMacroMouseMove = (e: SyntheticMouseEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const RIGHT_ZONE_WIDTH = 14; // 오른쪽 14px을 스크롤 영역으로 인식
    const isInRightZone = rect.width - x <= RIGHT_ZONE_WIDTH;

    if (isInRightZone) {
      setShowMacroScrollbar(true);
    }
  };

  const handleMacroMouseLeave = () => {
    // 마우스가 나가면 약간 있다가 숨기기
    if (macroScrollHideTimeoutRef.current) {
      clearTimeout(macroScrollHideTimeoutRef.current);
    }
    macroScrollHideTimeoutRef.current = setTimeout(() => {
      setShowMacroScrollbar(false);
    }, 400);
  };
  // [ ====== Auto Hide Scroll Bar Handlers End ====== ]

  return (
    <>
      {/* Shortcut View */}
      <Card
        style={{
          maxHeight: `${shortcutHeight}px`,
          overflowY: "hidden",
          textAlign: "center",
        }}
      >
        <Card.Header
          className="d-flex align-items-center justify-content-between py-1 px-2"
          style={{ fontSize: "1rem" }}
        >
          {/* 왼쪽: Shortcut 텍스트 */}
          <div className="d-flex align-items-center" style={{ gap: "4px" }}>
            <span
              style={{
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              Shortcut
            </span>
          </div>

          {/* 오른쪽: Edit 아이콘 버튼 + 접기 버튼 */}
          <div className="d-flex align-items-center" style={{ gap: "6px" }}>
            {/* Edit 버튼: 아이콘만 */}
            <button
              className="p-0 ms-0 border-0 bg-transparent text-muted icon-button"
              style={{ width: "26px", height: "26px" }}
              onClick={() => {
                setShowShortcutEditModal(true);
              }}
            >
              <PencilSquare width={14} height={14} />
            </button>

            {/* 접기 토글 버튼 */}
            <button
              className="p-0 me-1 border-0 bg-transparent text-muted icon-button"
              style={{ lineHeight: 1 }}
              onClick={() => setIsShortcutCollapsed((prev) => !prev)}
              aria-label="Toggle shortcuts"
            >
              {isShortcutCollapsed ? (
                <ChevronDown width={16} height={16} />
              ) : (
                <ChevronUp width={16} height={16} />
              )}
            </button>
          </div>
        </Card.Header>
        <Collapse in={!isShortcutCollapsed}>
          <div>
            <Card.Body className="m-0 py-2">
              <Container className="p-0 m-0">
                <Row>
                  {bindings.map((binding, idx) => (
                    <Button
                      key={idx}
                      variant="outline-secondary"
                      type="text"
                      style={styles.shortcutButton}
                      onClick={() => {
                        props.setShortcutInput(binding.text);
                        props.setShortcutInputIndex(
                          (prevShortcutInputIndex) => prevShortcutInputIndex + 1
                        );
                      }}
                    >
                      <div
                        title={binding.text}
                        style={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {binding.key} {binding.text}
                      </div>
                    </Button>
                  ))}
                </Row>
              </Container>
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Macro View */}
      <Card
        style={{
          marginTop: "5px",
          maxHeight: `${macroHeight}px`,
          overflowY: "hidden",
          textAlign: "center",
        }}
      >
        <Card.Header
          className="d-flex align-items-center justify-content-between py-1 px-2"
          style={{ fontSize: "1rem" }}
        >
          {/* 왼쪽: Macro 텍스트 */}
          <div className="d-flex align-items-center" style={{ gap: "4px" }}>
            <span
              style={{
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              Macro
            </span>
          </div>

          {/* 오른쪽: Edit 아이콘 버튼 + 접기 버튼 */}
          <div className="d-flex align-items-center" style={{ gap: "6px" }}>
            {/* Edit 버튼: 아이콘만 */}
            <button
              className="p-0 ms-0 border-0 bg-transparent text-muted icon-button"
              style={{ width: "26px", height: "26px" }}
              onClick={() => {
                setShowMacroEditModal(true);
              }}
              // TODO : 추후 Translator 창에서 Macro 편집 기능 구현 예정
              disabled
              hidden
            >
              <PencilSquare width={14} height={14} />
            </button>

            {/* 접기 토글 버튼 */}
            <button
              className="p-0 me-1 border-0 bg-transparent text-muted icon-button"
              style={{ lineHeight: 1 }}
              onClick={() => setIsMacroCollapsed((prev) => !prev)}
              aria-label="Toggle shortcuts"
            >
              {isMacroCollapsed ? (
                <ChevronDown width={16} height={16} />
              ) : (
                <ChevronUp width={16} height={16} />
              )}
            </button>
          </div>
        </Card.Header>
        <Collapse in={!isMacroCollapsed}>
          {/* <div style={{ overflowX: "hidden", overflowY: "auto" }}> */}
          <div
            className={`macro-scroll-container ${
              showMacroScrollbar ? "show-scrollbar" : ""
            }`}
            onScroll={handleMacroScroll}
            onWheel={handleMacroScroll} // 휠 스크롤에도 반응
            onMouseMove={handleMacroMouseMove}
            onMouseLeave={handleMacroMouseLeave}
          >
            <Card.Body className="pt-0">
              <Row>{macroList}</Row>
            </Card.Body>
          </div>
        </Collapse>
      </Card>
      <EditShortcutsView
        sessionId={props.sessionId}
        show={showShortcutEditModal}
        onHide={() => {
          setShowShortcutEditModal(false);
        }}
      />
    </>
  );
}
